import { useRef } from "react";
import { imgUrl } from "../lib/api";

export const FORMATIONS = ["4-3-3", "4-4-2", "4-2-3-1", "3-5-2", "3-4-3", "5-3-2", "Custom"];

export function formationSlots(f) {
  const rows = (FORMATIONS.includes(f) && f !== "Custom" ? f : "4-3-3").split("-").map(Number);
  const slots = [{ x: 50, y: 92 }];
  rows.forEach((n, ri) => {
    const y = 74 - (ri * 62) / Math.max(rows.length - 1, 1);
    for (let i = 0; i < n; i++) slots.push({ x: (100 / (n + 1)) * (i + 1), y });
  });
  return slots;
}

export function layoutPositions(lineup, events, tactics, formation) {
  if (!lineup) return {};
  const slots = formationSlots(formation || tactics?.formation || lineup.formation);
  const saved = tactics?.positions || {};
  const out = {};
  const slotOf = (pid) => {
    let idx = lineup.starting.indexOf(pid);
    if (idx < 0) {
      const sub = events?.find((e) => e.type === "substitution" && e.status === "active" && e.player_in_id === pid);
      if (sub) return slotOf(sub.player_id);
    }
    return slots[idx] || { x: 50, y: 50 };
  };
  (lineup.on_field || lineup.starting).forEach((pid) => {
    out[pid] = saved[pid] || slotOf(pid);
  });
  return out;
}

const Marker = ({ p, pos, st, onSelect, onDown, size, testid }) => (
  <button type="button" data-testid={testid}
    onPointerDown={onDown} onClick={onSelect}
    className="absolute flex -translate-x-1/2 -translate-y-1/2 select-none flex-col items-center gap-0.5 touch-none focus:outline-none"
    style={{ left: `${pos.x}%`, top: `${pos.y}%` }}>
    <div className="relative">
      <div className="overflow-hidden border-[3px] border-white bg-[#E7EAEE] shadow-lg transition-transform hover:scale-110" style={{ width: size, height: size, borderRadius: size * 0.3 }}>
        {imgUrl(p.photo) ? <img src={imgUrl(p.photo)} alt={p.short_name} draggable={false} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-bold">{p.number}</div>}
      </div>
      <span className="absolute -bottom-1 -left-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#17191C] px-1 text-[10px] font-bold text-white">{p.number}</span>
      {st?.yellow > 0 && !st?.red && <span data-testid={`yellow-marker-${p.number}`} className="absolute -right-1 -top-1 h-5 w-3.5 rounded-[2px] border border-white bg-[#FFD400] shadow" />}
      {st?.red && <span data-testid={`red-marker-${p.number}`} className="absolute -right-1 -top-1 h-5 w-3.5 rounded-[2px] border border-white bg-[#F02030] shadow" />}
      {st?.goals > 0 && <span className="absolute -right-2 bottom-0 rounded-full bg-white px-1 text-[9px] font-extrabold text-[#17191C] shadow">⚽{st.goals > 1 ? st.goals : ""}</span>}
    </div>
    <span className="max-w-[86px] truncate rounded bg-[#17191C]/85 px-1.5 py-0.5 text-[10px] font-bold text-white">{p.short_name}</span>
    <span className="text-[9px] font-bold uppercase tracking-wider text-white/90">{p.position}</span>
  </button>
);

export function Pitch({ players, positions, state = {}, onSelect, draggable, onMove, size = 52, testid = "tactical-pitch", children }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const move = (e) => {
    if (!drag.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = Math.min(96, Math.max(4, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.min(96, Math.max(4, ((e.clientY - r.top) / r.height) * 100));
    drag.current.moved = true;
    onMove?.(drag.current.pid, { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  };
  const up = () => {
    setTimeout(() => (drag.current = null), 0);
  };
  return (
    <div ref={ref} data-testid={testid} onPointerMove={draggable ? move : undefined} onPointerUp={up} onPointerLeave={up}
      className="tro-pitch relative mx-auto aspect-[68/90] w-full max-w-[560px] overflow-hidden rounded-xl border-4 border-white/80 shadow-inner">
      <svg viewBox="0 0 68 90" className="pointer-events-none absolute inset-0 h-full w-full" preserveAspectRatio="none">
        <g fill="none" stroke="rgba(255,255,255,.75)" strokeWidth=".35">
          <rect x="2" y="2" width="64" height="86" />
          <line x1="2" y1="45" x2="66" y2="45" />
          <circle cx="34" cy="45" r="7" />
          <rect x="16" y="2" width="36" height="13" /><rect x="25" y="2" width="18" height="5" />
          <rect x="16" y="75" width="36" height="13" /><rect x="25" y="83" width="18" height="5" />
          <path d="M28 15 A7 7 0 0 0 40 15" /><path d="M28 75 A7 7 0 0 1 40 75" />
        </g>
      </svg>
      {players.map((p) => positions[p.id] && (
        <Marker key={p.id} p={p} pos={positions[p.id]} st={state[p.id]} size={size} testid={`pitch-player-${p.number}`}
          onDown={draggable ? (e) => { e.preventDefault(); drag.current = { pid: p.id, moved: false }; } : undefined}
          onSelect={() => { if (!drag.current?.moved) onSelect?.(p); }} />
      ))}
      {children}
    </div>
  );
}
